import React, { useState } from 'react';
import { BellIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * EventRemindersTab - Manage reminders for an event
 *
 * Features:
 * - List current reminders
 * - Add new reminders
 * - Configure reminder type (notification, email, sms)
 * - Configure timing (minutes before event)
 *
 * Note: Backend reminder delivery is future work.
 * This tab sets up the configuration.
 */
const EventRemindersTab = ({ event, onUnsavedChanges }) => {
  const [reminders, setReminders] = useState(event?.reminders || []);
  const [isAdding, setIsAdding] = useState(false);
  const [newReminder, setNewReminder] = useState({
    reminderType: 'notification',
    minutesBefore: 30
  });

  const isNewEvent = !event?.id;

  // Preset timing options
  const timingOptions = [
    { value: 5, label: '5 minutes before' },
    { value: 10, label: '10 minutes before' },
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 120, label: '2 hours before' },
    { value: 1440, label: '1 day before' },
    { value: 10080, label: '1 week before' }
  ];

  const reminderTypes = [
    { value: 'notification', label: 'Push Notification', icon: '🔔' },
    { value: 'email', label: 'Email', icon: '✉️' },
    { value: 'sms', label: 'SMS', icon: '📱' }
  ];

  // Handle add reminder
  const handleAddReminder = () => {
    // Check if duplicate exists
    const exists = reminders.some(
      r => r.reminderType === newReminder.reminderType &&
           r.minutesBefore === newReminder.minutesBefore
    );

    if (exists) {
      return; // Don't add duplicate
    }

    const reminder = {
      id: `temp-${Date.now()}`, // Temporary ID for new reminders
      ...newReminder,
      isSent: false
    };

    setReminders(prev => [...prev, reminder]);
    setIsAdding(false);
    setNewReminder({ reminderType: 'notification', minutesBefore: 30 });
    if (onUnsavedChanges) onUnsavedChanges(true);
  };

  // Handle remove reminder
  const handleRemoveReminder = (reminderId) => {
    setReminders(prev => prev.filter(r => r.id !== reminderId));
    if (onUnsavedChanges) onUnsavedChanges(true);
  };

  // Format timing label
  const getTimingLabel = (minutes) => {
    const option = timingOptions.find(t => t.value === minutes);
    if (option) return option.label;

    if (minutes < 60) return `${minutes} minutes before`;
    if (minutes < 1440) return `${Math.round(minutes / 60)} hours before`;
    return `${Math.round(minutes / 1440)} days before`;
  };

  // Get reminder type label
  const getReminderTypeLabel = (type) => {
    const option = reminderTypes.find(t => t.value === type);
    return option ? `${option.icon} ${option.label}` : type;
  };

  if (isNewEvent) {
    return (
      <div className="p-6 text-center">
        <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Save Event First</h3>
        <p className="mt-1 text-sm text-gray-500">
          Create the event before adding reminders
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Add Reminder Button/Form */}
      {isAdding ? (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Add Reminder</h3>

          {/* Reminder Type */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">Remind via</label>
            <div className="flex gap-2">
              {reminderTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => setNewReminder(prev => ({ ...prev, reminderType: type.value }))}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    newReminder.reminderType === type.value
                      ? 'border-brand bg-brand-light text-gray-900'
                      : 'border-gray-300 hover:border-gray-400 text-gray-600'
                  }`}
                >
                  {type.icon} {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Timing */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">When</label>
            <select
              value={newReminder.minutesBefore}
              onChange={(e) => setNewReminder(prev => ({ ...prev, minutesBefore: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            >
              {timingOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsAdding(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={handleAddReminder}
              className="px-3 py-1.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors"
            >
              Add Reminder
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-gray-400 hover:text-gray-900 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Add Reminder
        </button>
      )}

      {/* Current Reminders */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Reminders ({reminders.length})
        </h3>

        {reminders.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <BellIcon className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No reminders set</p>
          </div>
        ) : (
          <div className="space-y-2">
            {reminders.map((reminder) => (
              <div
                key={reminder.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-light">
                    <BellIcon className="h-4 w-4 text-brand" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {getReminderTypeLabel(reminder.reminderType)}
                    </span>
                    <p className="text-xs text-gray-500">
                      {getTimingLabel(reminder.minutesBefore)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Sent indicator */}
                  {reminder.isSent && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      Sent
                    </span>
                  )}

                  {/* Remove button */}
                  <button
                    onClick={() => handleRemoveReminder(reminder.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove reminder"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
        <strong>Note:</strong> Reminder delivery (email/SMS) will be available soon.
        Push notifications work for logged-in users.
      </div>
    </div>
  );
};

export default EventRemindersTab;
